/**
 * MozBar — Moz's SEO toolbar
 *
 * Exposes two WAR groups with no `use_dynamic_url`: `icon/*.png` and `assets/*` to every
 * origin, and `content-scripts/toolbar.css` to all http(s) origins. Every path is static
 * and vendor-controlled, so each is fetchable from any page at a fixed URL.
 *
 * The icons are the cleanest signal: being images they load through `probeImage` with no
 * CORS grant, so they survive a strict page CSP.
 *
 * Manifest verified against version 5.0.16.
 */

import { probeImage, probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'eakacpaijcpapndcfffdgphdiccmpknp';

export const mozBar: ExtensionDefinition = {
  id: ID,
  name: 'MozBar',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 900_000,
  notes:
    'Static icons exposed to every origin plus a toolbar stylesheet on http(s) origins, ' +
    'no dynamic URL. The icon load is conclusive and needs no CORS grant.',
  signals: [
    {
      id: 'war-icon-128',
      kind: 'web-accessible-resource',
      description:
        'Load icon/128.png via <img> — a static icon exposed to every origin; needs no ' +
        'CORS grant, so it works even under a strict page CSP.',
      weight: 1,
      run: () => probeImage(ID, 'icon/128.png'),
    },
    {
      id: 'war-icon-48',
      kind: 'web-accessible-resource',
      description:
        'Load icon/48.png via <img> — a second icon, corroborating the identification.',
      weight: 0.5,
      run: () => probeImage(ID, 'icon/48.png'),
    },
    {
      id: 'war-toolbar-css',
      kind: 'web-accessible-resource',
      description:
        'Fetch content-scripts/toolbar.css — the injected toolbar stylesheet, a third ' +
        'independent path.',
      weight: 0.5,
      run: () => probeFetch(ID, 'content-scripts/toolbar.css'),
    },
  ],
};
